import React from 'react';
import { CacheableDataObject } from 'models/CacheableDataObject.jsx';
import PrivateChatBox from 'components/PrivateChatBox.jsx';

import { ChatMessage } from 'models/ChatMessage.jsx';
import { Request } from './Request';
import { MapHelper } from 'functions/MapHelper.jsx';
import { Users } from 'models/UserCollection.jsx';
import store from 'react/redux/store/store';
import { Dashboard } from "./Dashboard";
import * as privateChatActions from "../react/redux/actions/privateChat";

class PrivateChatMessageHolder extends CacheableDataObject {

	beforeConstruct() {
		this.currentOpenChat = null;
		this.chatsById = new Map();
		this.loadedClosedChats = false;
		this.closedChats = new Map();
		this.hasChatsToDisplay = false;

		this.retrievePrivateMessagesActivePromise = null;
		this.retrieveThroughIndex = -1;
		this.retrieveThroughPromise = null;

		this.updateDisplay = _.debounce(this.updateDisplay.bind(this), 100, {
			leading: true,
			trailing: true,
			maxWait: 1000
		});
	}

	retrievePrivateMessages(page = null) {
		const { privateChat } = store.getState();
		const { lastLoadedPrivateChatPage, loadedAllOpenPrivateChats } = privateChat;

		if(loadedAllOpenPrivateChats) {
			console.log('Already loaded all chats');
			return Promise.resolve();
		}
		if(page === null) {
			page = lastLoadedPrivateChatPage + 1;
		}

		if(this.retrievePrivateMessagesActivePromise) {
			return this.retrievePrivateMessagesActivePromise;
		}
		return this.retrievePrivateMessagesActivePromise = Request.apiv1Get('chat/direct_message_list', { page: page })
			.then((response) => this.applyPrivateMessagePage(page, response))
			.finally(() => {
				this.retrievePrivateMessagesActivePromise = null;
			});
	}

	/**
	 * Loads DM list pages in order until row `index` is loaded or the list runs
	 * out. Called again while loading, it raises the target of the load in progress.
	 */
	retrievePrivateMessagesThrough(index) {
		this.retrieveThroughIndex = Math.max(this.retrieveThroughIndex, index);
		if(this.retrieveThroughPromise) {
			return this.retrieveThroughPromise;
		}
		const loadNextPage = () => {
			if(store.getState().privateChat.loadedAllOpenPrivateChats || this.chatsById.size > this.retrieveThroughIndex) {
				return Promise.resolve();
			}
			return this.retrievePrivateMessages().then(loadNextPage);
		};
		return this.retrieveThroughPromise = loadNextPage()
			.finally(() => {
				this.retrieveThroughIndex = -1;
				this.retrieveThroughPromise = null;
			});
	}

	/**
	 * One page of the DM list, from GET chat/direct_message_list or, for page
	 * 1, from the startup payload's private_chats section.
	 */
	applyPrivateMessagePage(page, response) {
		const hasMore = !_.isEmpty(response.private_chat);
		store.dispatch(privateChatActions.loadedPrivateChatPage(page, hasMore, response.private_chat_total ?? null));
		Dashboard.performOpenSearchUpdate(response, 'private_chat');
		const { chat } = store.getState();

		if(page === 1) {
			const autoOpenPlayerId = chat.autoOpenedPrivateChat;
			if(autoOpenPlayerId) {
				const player = Users.retrieveById(autoOpenPlayerId);
				if(player) {
					this.openPrivateChat(player);
				}
			}
		}
		this.sortChats();
	}

	getClosedChats() {
		if(!this.loadedClosedChats) {
			this.loadedClosedChats = Request.apiv1Get('chat/direct_message_list', { open_chats: false })
				.then((response) => {
					this.parseInitialList(response.private_chat);
					return this.closedChats;
				}).finally(() => {
					this.loadedClosedChats = Promise.resolve(this.closedChats);
					this.updateDisplay();
				});
			this.updateDisplay();
		}
		return this.loadedClosedChats;
	}

	removeChat(chatBox) {
		if(this.currentOpenChat === chatBox) {
			this.currentOpenChat = null;
		}
		this.chatsById.delete(chatBox.player.id);
		this.updateDisplay();
	}

	setChatAsClosed(chatMessageBox) {
		this.currentOpenChat = null;
		store.dispatch(privateChatActions.setCurrentOpenPrivateChat(null));
		return false;
	}

	setChatAsOpen(chatMessageBox) {
		chatMessageBox.chatMessages.markAllRead();
		const previousOpen = this.currentOpenChat;
		this.currentOpenChat = chatMessageBox;
		if(previousOpen) {
			previousOpen.changed();
		}
		this.currentOpenChat.changed();
		store.dispatch(privateChatActions.setCurrentOpenPrivateChat(chatMessageBox.player.id));
		return true;
	}

	// The player read this conversation in another session (the
	// direct_message_read push), so its messages are no longer unread here.
	markConversationRead(otherPlayerId) {
		const id = Number(otherPlayerId);
		const chat = this.chatsById.get(id) || this.closedChats.get(id);
		if(!chat) {
			return;
		}
		chat.chatMessages.markAllRead();
		this.updateDisplay();
	}

	toggleChat(chatMessageBox) {
		if(this.isOpen(chatMessageBox)) {
			this.setChatAsClosed(chatMessageBox);
			return false;
		}
		else {
			this.setChatAsOpen(chatMessageBox);
			return true;
		}
	}

	changeChatRight() {
		let found = null;
		let current = null;
		for(let [i, chatRoom] of this.chatsById) {
			current = chatRoom;
			if(found) {
				current.setActive();
				return current;
			}
			if(current === this.currentOpenChat) {
				found = this.currentOpenChat;
			}
		}
		let result = this.chatsById.values().next();
		if(result.value) {
			console.log(result);
			console.log(result.value);
			result.value.setActive();
			return result.value;
		}
	}

	changeChatLeft() {
		let previous = null;
		let current = null;
		for(let [_, chatRoom] of this.chatsById) {
			current = chatRoom;
			if(current === this.currentOpenChat) {
				if(previous) {
					previous.setActive();
					return previous;
				}
				else {
					const last = Array.from(this.chatsById.values()).pop();
					last.setActive();
					return last;
				}
			}
			previous = chatRoom;
		}
	}

	isOpen(chatMessageBox) {
		return this.currentOpenChat === chatMessageBox;
	}

	chatWithPlayerIsOpen(id) {
		if(!this.currentOpenChat) {
			return false;
		}
		if(!id) {
			return false;
		}
		if(this.currentOpenChat.player.id == id) {
			return true;
		}
		return false;
	}

	parseInitialList(privateChats) {
		let player = null;

		_.each(privateChats, (chatData, otherPlayerId) => {
			const idToUse = chatData.id ? chatData.id : otherPlayerId; //The id confusingly comes in one of these forms.
			const otherPlayer = Users.retrieveById(idToUse);
			if(chatData.player) {
				otherPlayer.update(chatData.player);
			}
			else if(chatData.username) {
				otherPlayer.update({ username: chatData.username });
			}

			if(chatData.id) {
				for(let property in chatData) {
					if(otherPlayer.dataLocationParsers[property]) {
						const updateData = {};
						updateData[property] = chatData[property];
						otherPlayer.update(updateData);
					}
				}
			}
			let otherChatBox = this.updatePrivateChat({
				player: otherPlayer,
				isOpen: message ? message.is_open : true,
				blocked: chatData.blocked,
			});

			if(!otherChatBox.isBlocked && chatData.chat_messages) {
				const newMessageList = {};
				for(let chatMessageId in chatData.chat_messages) {
					const chatMessage = ChatMessage.create(chatData.chat_messages[chatMessageId]);
					if(chatData.notification) {
						chatMessage.notification = chatData.notification;
					}
					chatMessage.isNew = true;
					newMessageList[chatMessage.id] = chatMessage;
				}
				otherChatBox = this.updatePrivateChat({
					player: otherPlayer,
					messages: newMessageList,
					messagesAreNew: !!chatData.notification,
				});
			}
			if(chatData.chat_message_reactions) {
				for(let messageId in chatData.chat_message_reactions) {
					if(!chatData.chat_message_reactions.hasOwnProperty(messageId)) {
						continue;
					}
					otherChatBox.chatMessages.applyReactions(messageId, chatData.chat_message_reactions[messageId]);
				}
			}

			let lastMessage = chatData.last_message;
			let message = chatData.message;
			let messages = {};
			delete chatData.last_message;
			delete chatData.message;

			if(message) {
				const otherMessage = otherChatBox.chatMessages.fetchMessage(message.id);
				if(otherMessage) {
					otherMessage.isNew = true;
					// console.log('other message', otherMessage);
				}
				else {
					// console.log('had messag ereference but could not find other message..', message);
				}
			}
			else {
				// console.log('had no message reference');
			}
			if(lastMessage) {
				lastMessage = ChatMessage.create(lastMessage);
				messages[lastMessage.id] = lastMessage;
				if(message && message.unread) {
					lastMessage.isNew = true;
				}
			}
			this.updatePrivateChat({
				player: otherPlayer,
				messages: messages,
				isOpen: message ? message.is_open : true,
			});
		});
		MapHelper.inPlaceSort(this.closedChats, ([_, a], [__, b]) => {
			return PrivateChatMessageHolder.privateChatSort(a, b);
		});
	}

	sortedAllChatsById() {
		const chats = this.sortedChatsById();
		this.closedChats.forEach((value, key) => {
			chats.push([key, value]);
		});
		return chats;
	}

	sortedChatsById() {
		return Array.from(this.chatsById).sort(([_, a], [__, b]) => {
			return PrivateChatMessageHolder.privateChatSort(a, b);
		});
	}

	sortChats() {
		MapHelper.inPlaceSort(this.chatsById, ([_, a], [__, b]) => {
			return PrivateChatMessageHolder.privateChatSort(a, b);
		});
		this.updateDisplay();
	}

	static privateChatSort(a, b) {
		const aChat = a.chatMessages;
		const bChat = b.chatMessages;
		const aLast = aChat.getLastMessage();
		const bLast = bChat.getLastMessage();

		if(aLast || bLast) {
			if(aLast && bLast) {
				return aLast.time.isAfter(bLast.time) ? -1 : 1;
			}
			if(!aLast) {
				return 1;
			}
			if(!bLast) {
				return -1;
			}
		}

		return a.player.id > b.player.id ? 1 : -1;
	}

	openPrivateChat(player) {
		const chat = this.updatePrivateChat({ player });
		if(chat) {
			this.setChatAsOpen(chat);
		}
		else {
		}
	}

	/**
	 *  player or PrivateChat are used as the reference to update,
	 *  if only player is provided, isOpen needs to be provided so that we can know which list to update
	 */
	updatePrivateChat({ player, privateChat, messages, messagesAreNew, isOpen = true, blocked }) {
		let potentialChat = null;
		const activeList = isOpen ? this.chatsById : this.closedChats;
		const inactiveList = isOpen ? this.closedChats : this.chatsById;

		if(player && player.id) {
			potentialChat = activeList.get(player.id);
			if(!potentialChat) {
				potentialChat = PrivateChatBox.create();
				potentialChat.setChatMessageHolder(this);
				potentialChat.setPlayer(player);

				let allPrevious = null;
				if(isOpen) {
					//This is to make sure they get added to the front of the private chat list instead of the end.
					allPrevious = Array.from(activeList);
					activeList.clear();
				}
				activeList.set(player.id, potentialChat);
				if(isOpen) {
					for(let [id, chat] of allPrevious) {
						activeList.set(id, chat);
					}
				}
			}
			inactiveList.delete(player.id);
		}
		else if(privateChat) {
			if(!(privateChat instanceof PrivateChatBox)) {
				throw new Error('Private chat needs to be a private chat box! we got', typeof privateChat);
			}
			potentialChat = privateChat;
		}
		else {
			console.log(player);
			throw new Error('we are not ready for this yet, perhaps request the id from the server before attempting this');
		}

		if(blocked !== undefined) {
			potentialChat.isBlocked = blocked;
		}

		if(messages) {
			potentialChat.chatMessages.addMessages({ messages: messages, messagesAreNew });
		}
		potentialChat.privateChatMessageHolder = this;

		this.updateDisplay();
		return potentialChat;
	}

	updateDisplay(forceScrollToBottom = false) {
		store.dispatch(privateChatActions.privateChatListUpdate(this.chatsById, this.closedChats));

		const start = this.hasChatsToDisplay;
		this.hasChatsToDisplay = !!this.chatsById.size;
		const changed = start != this.hasChatsToDisplay;

		if(changed) {
			setTimeout(function() {
				$(window).trigger('resize');
			}, 1000);
		}

		if(forceScrollToBottom && this.currentOpenChat) {
			this.currentOpenChat.chatMessages.scrollToBottomOnce = true;
		}

	}

}

const privateChatMessageHolder = PrivateChatMessageHolder.create();
export { privateChatMessageHolder, PrivateChatMessageHolder };